import React, { useEffect, useState } from 'react';
import { Globe, Eye, EyeOff, Save } from 'lucide-react';
import { api } from '../../App';
import toast from 'react-hot-toast';
import Button from '@shared/ui/Button';

/* Public profile + citizen-directory discoverability (roadmap Phase 2 §6, D-2).
   GET/PUT /organizations/:id/public-profile — org admins only. While the
   toggle is OFF the org is invisible to citizen follow/discover; published
   positions still reach existing members. */

interface PublicProfile {
  discoverable: boolean;
  description: string | null;
  website_url: string | null;
  contact_email: string | null;
  contact_phone: string | null;
}

interface PublicProfileCardProps {
  organizationId: string;
}

const inputClass =
  'w-full px-3 py-2 rounded-lg text-sm border border-border-light-secondary dark:border-border-dark-secondary bg-white dark:bg-background-dark-secondary text-neutral-900 dark:text-text-dark-primary focus:outline-none focus:ring-2 focus:ring-purple-500';

const PublicProfileCard: React.FC<PublicProfileCardProps> = ({ organizationId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [discoverable, setDiscoverable] = useState(false);
  const [form, setForm] = useState({ description: '', website_url: '', contact_email: '', contact_phone: '' });

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/organizations/${organizationId}/public-profile`);
        const p = (res.data?.data?.profile ?? {}) as PublicProfile;
        if (cancelled) return;
        setDiscoverable(Boolean(p.discoverable));
        setForm({
          description: p.description || '',
          website_url: p.website_url || '',
          contact_email: p.contact_email || '',
          contact_phone: p.contact_phone || '',
        });
      } catch (err) {
        console.error('Failed to fetch public profile:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const save = async (next?: { discoverable?: boolean }) => {
    setSaving(true);
    try {
      const res = await api.put(`/organizations/${organizationId}/public-profile`, {
        discoverable: next?.discoverable ?? discoverable,
        description: form.description || null,
        website_url: form.website_url || null,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
      });
      if (res.data?.success) toast.success('Public profile saved');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to save public profile');
      // Re-sync the toggle if the write failed
      if (next?.discoverable !== undefined) setDiscoverable(!next.discoverable);
    } finally {
      setSaving(false);
    }
  };

  const toggleDiscoverable = () => {
    const next = !discoverable;
    setDiscoverable(next);
    save({ discoverable: next });
  };

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div className="bg-white dark:bg-background-dark-primary rounded-xl border border-border-light dark:border-border-dark-hairline p-6">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          <h3 className="text-base font-semibold text-neutral-900 dark:text-text-dark-primary">Public profile</h3>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={discoverable}
          disabled={loading || saving}
          onClick={toggleDiscoverable}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
            discoverable
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
              : 'bg-background-tertiary text-neutral-700 dark:bg-background-dark-secondary dark:text-text-dark-secondary'
          }`}
        >
          {discoverable ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          {discoverable ? 'Discoverable by citizens' : 'Hidden from citizen directory'}
        </button>
      </div>
      <p className="text-sm text-neutral-500 dark:text-text-dark-tertiary mb-5">
        When discoverable, citizens can find and follow your organization from Capitol Commons and see the positions
        you publish. Your profile describes who you are; the toggle controls directory visibility.
      </p>

      {loading ? (
        <p className="text-sm text-neutral-500 dark:text-text-dark-tertiary">Loading…</p>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-neutral-700 dark:text-text-dark-secondary mb-1.5">Description</label>
            <textarea
              value={form.description}
              onChange={set('description')}
              rows={3}
              maxLength={2000}
              placeholder="What your organization works on, in a sentence or two."
              className={`${inputClass} resize-none`}
            />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-neutral-700 dark:text-text-dark-secondary mb-1.5">Website</label>
              <input value={form.website_url} onChange={set('website_url')} placeholder="https://…" className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-700 dark:text-text-dark-secondary mb-1.5">Contact email</label>
              <input value={form.contact_email} onChange={set('contact_email')} type="email" placeholder="hello@example.org" className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-700 dark:text-text-dark-secondary mb-1.5">Contact phone</label>
              <input value={form.contact_phone} onChange={set('contact_phone')} placeholder="(303) 555-0100" className={inputClass} />
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="primary" size="sm" icon={Save} loading={saving} onClick={() => save()}>
              {saving ? 'Saving…' : 'Save profile'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicProfileCard;
